import { ChangeDetectionStrategy, Component, Input, booleanAttribute } from '@angular/core';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { IconComponent } from '../icon/icon.component';

/**
 * Names the timezone a screen's times are expressed in, as a chip that reads at a glance.
 *
 * A converted "HH:mm" is meaningless without its zone: a manager in Brisbane and one in Kolkata read the
 * same clock-in as two different numbers, each correct for them, and nothing on the page tells them apart —
 * so a disagreement about a time cannot be settled, and a screenshot loses the one fact needed to read it.
 * Every screen that converts must therefore say what it converted into, and say it visibly rather than as a
 * grey caption people skim past.
 *
 * Naming the zone still leaves "why THIS zone?" unanswered, and the answer differs per screen (the
 * reader's own, the employee's, the work location's). Pass `info` to hang that explanation off an ⓘ so a
 * reader who wonders can find out without the chip growing into a paragraph.
 *
 * ```html
 * <app-timezone-note [zone]="timezoneName()" prefix="All times in" />
 * <app-timezone-note [zone]="data?.timezoneName" prefix="Enter times in" [suffix]="ownerPossessive + ' timezone'" />
 * <app-timezone-note [zone]="timezoneName()" [info]="'Your own timezone, from your profile.'" />
 * ```
 *
 * Renders nothing when `zone` is empty — an unknown zone is left unstated rather than guessed at.
 */
@Component({
  selector: 'app-timezone-note',
  standalone: true,
  imports: [IconComponent, NgbTooltipModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (zone) {
      <span class="tz-note" [class.tz-note-block]="block">
        <app-icon name="clock" [size]="13" />
        <div class="tz-note-text">
          @if (prefix) {
            <span class="tz-note-prefix">{{ prefix }}</span>
          }
          <strong class="tz-note-zone">{{ zone }}</strong>
          @if (suffix) {
            <span class="tz-note-suffix">— {{ suffix }}</span>
          }
        </div>
        @if (info) {
          <!-- A button, not a bare span: a tooltip on a non-focusable element is unreachable by keyboard,
               and ngbTooltip only sets aria-describedby, so the control still needs its own name. -->
          <button
            type="button"
            class="tz-note-info"
            [ngbTooltip]="info"
            aria-label="Where this timezone comes from"
          >
            <app-icon name="info" [size]="13" />
          </button>
        }
      </span>
    }
  `,
  styles: [
    `
      .tz-note {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 4px 10px;
        border-radius: 10px;
        font-size: 12px;
        line-height: 1.4;
        color: var(--h4c-dark-blue, #262262);
        background: rgba(39, 170, 225, 0.1);
        border: 1px solid rgba(39, 170, 225, 0.35);
      }
      /* Own line, when the chip sits above a block of times rather than inline in a header row. */
      .tz-note-block {
        display: flex;
        width: fit-content;
        max-width: 100%;
      }
      .tz-note-prefix,
      .tz-note-suffix {
        color: var(--fg-2, #64748b);
      }
      .tz-note-zone {
        font-weight: 700;
      }
      .tz-note-text {
        display: inline-flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 4px;
      }
      app-icon {
        color: var(--h4c-bright-blue, #27aae1);
        display: inline-flex;
      }
      .tz-note-info {
        display: inline-flex;
        align-items: center;
        padding: 0;
        border: 0;
        background: none;
        cursor: help;
        color: var(--fg-2, #64748b);
      }
      .tz-note-info:hover,
      .tz-note-info:focus-visible {
        color: var(--h4c-bright-blue, #27aae1);
      }
      .tz-note-info app-icon {
        color: inherit;
      }
    `,
  ],
})
export class TimezoneNoteComponent {
  /** Display name of the zone, e.g. "(UTC+10:00) Brisbane". Empty renders nothing. */
  @Input() zone?: string | null;
  /** Leading words, e.g. "All times in". */
  @Input() prefix?: string;
  /** Trailing clause after an em dash, e.g. "Rohan Desai's timezone". */
  @Input() suffix?: string;
  /** Put the chip on its own line instead of flowing inline. Usable as a bare attribute: `block`. */
  @Input({ transform: booleanAttribute }) block = false;
  /**
   * Where the zone came from, shown on an ⓘ. Each screen resolves its zone differently, so the wording
   * belongs to the caller — say whose zone it is and which setting decides it, not just that one exists.
   * Omit to render no icon.
   */
  @Input() info?: string;
}
